import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import './index.scss';
import CoverPage from './children/CoverPage';
import ResponseRatePage from './children/ResponseRatePage';
import AboutPage from './children/AboutPage';
import OutlinePage from './children/OutlinePage';
import ResultsSummaryPage from './children/ResultsSummaryPage';
import TopStrengthsPage from './children/TopStrengthsPage';
import CommentsPage from './children/CommentsPage';
import DevelopmentAreasPage from './children/DevelopmentAreasPage';
import TwoByTwoMatrixPage from './children/TwoByTwoMatrixPage';
import KeyTrendsPage from './children/KeyTrendsPage';
import AppendixTrendPage from './children/AppendixTrendPage';
import WordsOfWisdomPage from './children/WordsOfWisdomPage';
import AppendixIntroPage from './children/AppendixPage';
import StandardDeviationPage from './children/StandardDeviationPage';
import EditorToolbar from './children/EditorToolbar';
import {
  getResultData,
  initReportData,
  loadReportData,
  setReportType,
} from '../../redux/actions';
import { SelectReportState, selectSectionStartPages } from './Selector';
import GraphLoader from '../../Components/ContentLoaders/GraphLoader';
import { EmptyState } from '../../Packages/EmptyState';

// Font sizes used by the circumplex chart on the PDF Results Summary page.
// The chart is visually scaled down to fit A4 (~0.6×), so we bump the native
// font sizes here to compensate. Tweak these freely — they're applied directly
// to amCharts before the chart renders, so the dashboard rendering is unaffected.
const PDF_CIRCUMPLEX_FONT_SIZES = {
  subCategoryFontSize: 9, // radar outer labels (e.g. "Optimize Talent Potential")
  axisFontSize: 10,        // radar 1-6 scale numbers
  categoryFontSize: 23,    // donut category labels (COACHING / COLLABORATION / …)
};

function RenderPdfBody(props) {
  const { editable, initReportData, loadReportData, pdfConfigLoading, pdfConfigError, isDataFetched, getResultData, startPages, reportType = "standard" } = props;
  let data = reportType === "standard" || !editable ? props.data : props.trendData
  let dirty = reportType === "standard" || !editable ? props.dirty : props.trendDirty
  useEffect(() => {
    props.setReportType({ reportType: reportType })
    // const timer = setTimeout(() => {
    if (!isDataFetched) {
      initReportData(editable, reportType);

      if (editable) {
        loadReportData();
      } else {
        // const HI = window.__PDF_DATA__ || {};
        const params = new URLSearchParams(window.location.search);
        const projectId = params.get("projectId");

        getResultData({
          projectId: projectId,
        });
      }
    }
    // }, 4000);

    // return () => clearTimeout(timer);
  }, [reportType]);

  useEffect(() => {
    if (!editable || !dirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [editable, dirty]);
  const getPageData = (pageContent) => data.find(d => d.pageContent === pageContent);
  const getPageDataAll = (pageContent) => data.filter(d => d.pageContent === pageContent);

  const startingPage = getPageData('startingPage');
  const firstPage = getPageData('firstPage');
  const personName = (startingPage && startingPage.projectName) || '';

  const aboutPage = getPageData('aboutPage');
  const outlinePage = getPageData('outlineOfThisReport');
  const circumplexData = getPageData('circumplexData');
  const topStrengths = getPageData('topStrengths');
  const strengthComments = getPageData('strenghtComments');
  const developmentAreas = getPageData('keyDevelopmentAreas');
  const developmentComments = getPageData('devlopmentAreasComments');
  const twoByTwoMatrix = getPageData('twoByTwoMatrix');
  const keyTrends = getPageData('trendGainLoss');
  const appendixTrend = getPageData('appendixForApplied');
  const wordsOfWisdom = getPageData('wordsOfWisdom');
  const ratingsIntro = getPageData('ratingsOnEachBehavior');
  const stdDeviationSections = getPageDataAll('standardDeviationSection');
  let pageCounter = startPages.standardDeviationSection;

  return (
    <>
      <div className={`render-pdf-container${editable ? ' report-border' : ''}`}>
        {
          pdfConfigLoading ? <GraphLoader height={1000} width={600} /> : pdfConfigError || data.length === 0 ? < EmptyState emptyStateMessage="No data found" /> : <>
            {startingPage && <CoverPage data={startingPage} firstPageData={firstPage} reportType={reportType} />}

            {firstPage && <ResponseRatePage data={firstPage} personName={personName} startPageNumber={startPages.firstPage} reportType={reportType} />}

            {aboutPage && <AboutPage data={aboutPage} personName={personName} pageNumber={startPages.aboutPage} />}

            {outlinePage && <OutlinePage data={outlinePage} personName={personName} pageNumber={startPages.outlineOfThisReport} reportType={reportType} />}

            {circumplexData && (
              <ResultsSummaryPage
                data={circumplexData}
                personName={personName}
                chartFontSizes={PDF_CIRCUMPLEX_FONT_SIZES}
                pageNumber={startPages.circumplexData}
                reportType={reportType}
              />
            )}

            {topStrengths && <TopStrengthsPage data={topStrengths} personName={personName} startPageNumber={startPages.topStrengths} reportType={reportType} />}

            {strengthComments && (
              <CommentsPage
                data={strengthComments}
                personName={personName}
                sectionNumber={3}
                title="EXAMPLES OF STRENGTHS"
                pageNumber={startPages.strenghtComments}
                configKey="strenghtComments"
              />
            )}

            {developmentAreas && <DevelopmentAreasPage data={developmentAreas} personName={personName} startPageNumber={startPages.keyDevelopmentAreas} reportType={reportType} />}

            {developmentComments && (
              <CommentsPage
                data={developmentComments}
                personName={personName}
                sectionNumber={5}
                title="DEVELOPMENT AREAS COMMENTS"
                pageNumber={startPages.devlopmentAreasComments}
                configKey="devlopmentAreasComments"
              />
            )}

            {reportType === "trend" && keyTrends && (
              <KeyTrendsPage
                data={keyTrends}
                personName={personName}
                startPageNumber={startPages.trendGainLoss}
              />
            )}

            {twoByTwoMatrix && <TwoByTwoMatrixPage data={twoByTwoMatrix} personName={personName} pageNumber={startPages.twoByTwoMatrix} reportType={reportType} />}

            {reportType === "trend" && appendixTrend && (
              <AppendixTrendPage
                data={appendixTrend}
                personName={personName}
                startPageNumber={startPages.appendixForApplied}
              />
            )}

            {wordsOfWisdom && <WordsOfWisdomPage data={wordsOfWisdom} personName={personName} startPageNumber={startPages.wordsOfWisdom} reportType={reportType} />}

            {ratingsIntro && <AppendixIntroPage data={ratingsIntro} personName={personName} pageNumber={startPages.ratingsOnEachBehavior} reportType={reportType} />}

            {stdDeviationSections.map((section, idx) => (
              <StandardDeviationPage
                key={section.dimension || idx}
                data={section}
                personName={personName}
                pageNumber={pageCounter + idx}
              />
            ))}
            {editable && <EditorToolbar />}


          </>
        }

      </div>
    </>
  );
}

const mapStateToProps = (state, ownProps) => {
  const slice = SelectReportState(state);
  return {
    reportType: slice.reportType,
    trendData: slice.trendData,
    data: slice.data,
    editable: ownProps.editable || slice.editable,
    dirty: slice.dirty,
    trendDirty: slice.trendDirty,
    pdfConfigLoading: slice.isProcessing,
    pdfConfigError: slice.error,
    isDataFetched: slice.isDataFetched,
    startPages: selectSectionStartPages(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  initReportData: (editable, reportType) => dispatch(initReportData(editable, reportType)),
  loadReportData: () => dispatch(loadReportData()),
  getResultData: (payload) => dispatch(getResultData(payload)),
  setReportType: (payload) => dispatch(setReportType(payload))
});

export default connect(mapStateToProps, mapDispatchToProps)(RenderPdfBody);
